import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { LinkedinTypes } from 'enums';
import { AddContact } from 'features/co-pilot/co-pilot.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;
  loading: boolean;
  contact: AddContact;

  linkedinAccount: LinkedinAccount | undefined;

  handleSingleContactFieldChange(type: keyof AddContact): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleClose(): void;

  handleAddSingleContact(): void;
}

const AddSingleContactDialog: React.FC<Props> = ({
  open,
  loading,
  contact,
  linkedinAccount,
  handleSingleContactFieldChange,
  handleClose,
  handleAddSingleContact,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { linkedinUrl, firstName, lastName, company, email, phoneNumber } = contact;

  const accountIsValid = useMemo(() => {
    switch (linkedinAccount?.liProductType) {
      case LinkedinTypes.LINKEDIN:
        return linkedinUrl.includes('/in/') || linkedinUrl.includes('/sales/') || linkedinUrl.includes('/talent/');

      case LinkedinTypes.SALES_NAV:
        return linkedinUrl.includes('/sales/') || linkedinUrl.includes('/in/');

      case LinkedinTypes.RECRUITER:
        return linkedinUrl.includes('/talent/') || linkedinUrl.includes('/in/');

      default:
        return false;
    }
  }, [linkedinUrl, linkedinAccount]);

  const hasError = !accountIsValid && linkedinUrl.trim() !== '';

  const mapLiProductType = (product: LinkedinTypes | undefined) => {
    switch (product) {
      case LinkedinTypes.LINKEDIN:
        return 'LinkedIn';

      case LinkedinTypes.SALES_NAV:
        return 'Sales Navigator';

      case LinkedinTypes.RECRUITER:
        return 'Recruiter';

      default:
        return 'LinkedIn';
    }
  };

  const errorText = !accountIsValid
    ? t('invalidUrlForLinkedinAccountWarning', {
        product:
          linkedinAccount?.liProductType === LinkedinTypes.LINKEDIN
            ? `LinkedIn ${t('or')} Sales Navigator ${t('or')} Recruiter`
            : `${mapLiProductType(linkedinAccount?.liProductType)} ${t('or')} LinkedIn`,
      })
    : undefined;

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('addSingleContactDialogTitle')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          <PaiTextField
            value={linkedinUrl}
            label={`${t('linkedinUrl')}*`}
            placeholder={t('enterLinkedinUrlHere')}
            variant={'background'}
            onChange={handleSingleContactFieldChange('linkedinUrl')}
            error={hasError}
            errorText={errorText}
          />
          <PaiTextField
            value={firstName}
            label={`${t('firstName')} (${t('optional')})`}
            placeholder={t('enterFirstNameHere')}
            variant={'background'}
            onChange={handleSingleContactFieldChange('firstName')}
          />
          <PaiTextField
            value={lastName}
            label={`${t('lastName')} (${t('optional')})`}
            placeholder={t('enterLastNameHere')}
            variant={'background'}
            onChange={handleSingleContactFieldChange('lastName')}
          />
          <PaiTextField
            value={company}
            label={`${t('company')} (${t('optional')})`}
            placeholder={t('enterCompanyHere')}
            variant={'background'}
            onChange={handleSingleContactFieldChange('company')}
          />
          <PaiTextField
            value={email}
            label={`${t('email')} (${t('optional')})`}
            placeholder={t('enterEmailHere')}
            variant={'background'}
            onChange={handleSingleContactFieldChange('email')}
          />
          <PaiTextField
            value={phoneNumber || ''}
            label={`${t('phoneNumber')} (${t('optional')})`}
            placeholder={t('enterPhoneNumberHere')}
            variant={'background'}
            onChange={handleSingleContactFieldChange('phoneNumber')}
          />
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton
          text={t('add')}
          size={'small'}
          border={'rounded'}
          action={handleAddSingleContact}
          loading={loading}
          disabled={!linkedinUrl || hasError}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '24px !important',
    whiteSpace: 'pre-wrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default AddSingleContactDialog;
